import React, { useCallback, useMemo, useState } from 'react'
import Resx from '@/components/Resx'
import { MainBody, WhiteBox, Main, MainTop } from '@/components/Layout'
import {
  LookupCell,
  createLookupFilterCell,
  DateCell,
  DateFilterCell,
  LookupNameColumn, BooleanColumn
} from '@/components/BsrsGrid'
import { GridColumn as Column } from '@progress/kendo-react-grid'
import { FormLoader, PropertyRenderer } from '@/components/changeRequests'
import context from '@/context'
import { ValidationBoundary } from '@/components/validation'
import { TabNavItems, TabContent, TabPane } from '@/components/tabs'
import { dialogTypes } from '@/dialogs'
import { AddressesIndividual } from '@/components/demographics/addresses'
import { IndividualContacts } from '@/components/demographics/contacts'
import { IndividualGovernmentService } from '@/components/demographics/governmentService'
import { Row, Col } from 'reactstrap'
import { ScopeDetailHeader } from '@/components/changeRequests/scopeDetailHeader'
import { UserAccountInfo } from '@/internal-app/pages/individuals/userAccountInfo'
import { getBadges } from '@/components/changeRequests/badgeUtils'
import DownloadCbsReportButton from './cbsReports'
import { useLocalization } from '@/hooks/localization'
import { modes } from '@/components/changeRequests/propertyRenderer'
import { individualPermissionTypes } from '@/enums/individualPermissionTypes'
import { PersonalData } from '@/components/demographics/personalData'
import { IndividualBlacklists } from '@/components/demographics/blacklists'
import { CbiBlacklists } from '@/components/demographics/cbiBlacklists'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import { IndividualChangeRequestHistory } from '@/components/demographics/changeRequestHistory'
import { IndividualUserHistory } from '@/components/demographics/individualUserHistory'
import { useIndividualPermissionChecker } from '@/hooks/useIndividualPermissionChecker'
import { usePropertyReader } from '@/hooks/usePropertyReader'
import { permissionTypes } from '@/enums/permissionTypes'
import { IndividualPermissionProvider } from '@/components/changeRequests/individualPermissionProvider'

const IndividualDetail = React.memo(function CompanyDetail (props) {
  const loader = useCallback(id => context.api.individualService.getIndividualById(id), [])
  const renderForm = useCallback(formProps => (<IndividualDetailForm {...formProps} />), [])
  return (
    <FormLoader {...props} loader={loader} render={renderForm} />
  )
})

const renderCbsReports = (targetId) => {
  return (
    <WhiteBox header='Individuals.CbsReports'>
      <DownloadCbsReportButton individualId={targetId} />
    </WhiteBox>
  )
}

const IndividualDetailForm = React.memo(function IndividualDetailForm (props) {
  const { targetId, mode, data, isChangeItemId } = props
  const { localize, locale } = useLocalization()
  const [relatedInstitutionIds] = usePropertyReader(['relatedInstitutionIds'], 'originalData')
  const getIndividualPermission = useIndividualPermissionChecker(relatedInstitutionIds, isChangeItemId)
  const isGranted = usePermissionChecker()
  const [activeTab, setActiveTab] = useState('1')
  const getPersonalIds = useCallback(req => context.api.individualService.getPersonalIds(targetId, req), [targetId])
  const getAddresses = useCallback(req => context.api.individualService.getAddresses(targetId, req), [targetId])
  const getContactInformation = useCallback(req => context.api.individualService.getContactInformation(targetId, req), [targetId])
  const getBans = useCallback(req => context.api.individualService.getBans(targetId, req), [targetId])
  const getJobs = useCallback(req => context.api.individualService.getJobs(targetId, req), [targetId])
  const getCourses = useCallback(req => context.api.individualService.getCourses(targetId, req), [targetId])
  const getEducations = useCallback(req => context.api.individualService.getEducations(targetId, req), [targetId])
  const getExperiences = useCallback(req => context.api.individualService.getExperiences(targetId, req), [targetId])
  const getRelatedPersons = useCallback(req => context.api.individualService.getRelatedPersons(targetId, req), [targetId])
  const getChangeRequestHistory = useCallback(req => context.api.changeRequestService.getHistoryForEntity(targetId, 'Individuals', req), [targetId])
  const getUserAccountHistory = useCallback(req => context.api.individualService.getUserAccountHistory(targetId, req), [targetId])

  const emptyValue = useMemo(() => <i><Resx name='Common.NoDescription' /></i>, [])

  const permissionPredicate = useCallback(x => !x.permission ||
    getIndividualPermission(x.permission) !== permissionTypes.enum.None,
  [getIndividualPermission])
  const canViewChangeHistory = isGranted('CanViewChangeHistory')

  return (
    <Main path={[{ text: <PropertyRenderer name='name' readonly /> }]}>
      <MainTop>
        <ScopeDetailHeader title='Individuals.DetailTitle' backUrl='/individuals'>
          <PropertyRenderer name='name' readonly />
        </ScopeDetailHeader>
      </MainTop>
      <MainBody transparent>
        <ValidationBoundary>
          <Row>
            <Col xl='7' className='white-box-full-height'>
              <PersonalData targetId={targetId} mode={mode} />
            </Col>
            <Col xl='5' className='white-box-full-height'>
              {targetId && isGranted('CanManageUsers') && <UserAccountInfo id={targetId} />}
              {getIndividualPermission(individualPermissionTypes.enum.IndividualCbsReport) !== permissionTypes.enum.None &&
                mode === modes.view &&
                targetId && renderCbsReports(targetId)}
              <WhiteBox header='Individuals.Description'>
                <PropertyRenderer name='description' inputType='textarea' emptyValue={emptyValue} />
              </WhiteBox>
              <WhiteBox header='Individuals.Expertise'>
                <PropertyRenderer name='expertise' inputType='textarea' emptyValue={emptyValue} />
              </WhiteBox>
            </Col>
          </Row>
          <Row>
            <Col>
              <WhiteBox>
                <TabNavItems
                  buttonView
                  activeTabId={activeTab}
                  onClick={setActiveTab}
                  items={getBadges(mode, data, [
                    { id: '1', text: 'Individuals.PersonalIds', badge: 'personalIds', permission: individualPermissionTypes.enum.IndividualPersonalIds },
                    { id: '2', text: 'Persons.Addresses', badge: 'addresses', permission: individualPermissionTypes.enum.IndividualAddresses },
                    { id: '3', text: 'Persons.Contacts', badge: 'contacts', permission: individualPermissionTypes.enum.IndividualContacts },
                    { id: '4', text: 'Individuals.Bans', badge: 'bans', permission: individualPermissionTypes.enum.IndividualBans },
                    { id: '5', text: 'Individuals.Employee', badge: 'jobs', permission: individualPermissionTypes.enum.IndividualCbiEmployee },
                    { id: '6', text: 'Individuals.Courses', badge: 'courses', permission: individualPermissionTypes.enum.IndividualCourses },
                    { id: '7', text: 'Individuals.Education', badge: 'educations', permission: individualPermissionTypes.enum.IndividualEducation },
                    { id: '8', text: 'Individuals.Experience', badge: 'experiences', permission: individualPermissionTypes.enum.IndividualExperience },
                    { id: '9', text: 'Individuals.RelatedInstitutions', badge: 'relatedPersons', permission: individualPermissionTypes.enum.IndividualRelatedPersons },
                    { id: '10', text: 'Individuals.GovernmentService', badge: 'governmentService', permission: individualPermissionTypes.enum.IndividualGovernmentService },
                    { id: '11', text: 'Blacklists.BlacklistTitle', badge: 'blacklists', readonly: true, permission: individualPermissionTypes.enum.IndividualBlacklists },
                    (!isChangeItemId) && { id: '12', text: 'Blacklists.CbiBlacklistTitle', badge: 'cbiBlacklists', permission: individualPermissionTypes.enum.IndividualCbiBlacklists },
                    (canViewChangeHistory && !isChangeItemId) && { id: '13', text: 'Institutions.ChangeRequestHistory', badge: 'chrHistory', readonly: true, permission: individualPermissionTypes.enum.IndividualChangeHistory },
                    isGranted('CanManageUsers') && { id: '14', text: 'Individuals.UserAccountHistory', badge: 'userAccountsCount', readonly: true, permission: individualPermissionTypes.enum.IndividualUserAccounts }
                  ].filter(permissionPredicate).filter(Boolean))} />
                <TabContent activeTab={activeTab} renderActiveOnly>
                  <TabPane tabId='1'>
                    <IndividualPermissionProvider individualPermission={individualPermissionTypes.enum.IndividualPersonalIds}>
                      <PropertyRenderer
                        name='personalIds' inputType='grid'
                        addButtonLabel='Individuals.AddPersonalId'
                        detailDialog={{ dialogType: dialogTypes.personalId }}
                        dataSource={getPersonalIds}>
                        <Column field='type' title={localize('Individuals.PersonalIdType')} cell={LookupCell} width='200px'
                          filterCell={createLookupFilterCell(context.cache.getPersonalIdTypes)} />
                        <Column field='number' title={localize('Individuals.PersonalIdNumber')} width='200px' />
                        <Column field='validFrom' title={localize('Common.ValidFrom')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                        <Column field='validTo' title={localize('Common.ValidTo')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                      </PropertyRenderer>
                    </IndividualPermissionProvider>
                  </TabPane>
                  <TabPane tabId='2'>
                    <AddressesIndividual dataSource={getAddresses} />
                  </TabPane>
                  <TabPane tabId='3'>
                    <IndividualContacts dataSource={getContactInformation} />
                  </TabPane>
                  <TabPane tabId='4'>
                    <IndividualPermissionProvider individualPermission={individualPermissionTypes.enum.IndividualBans}>
                      <PropertyRenderer
                        name='bans' inputType='grid'
                        addButtonLabel='Individuals.AddBan'
                        detailDialog={{ dialogType: dialogTypes.ban }}
                        dataSource={getBans}>
                        <Column field='type' title={localize('Individuals.BanType')} cell={LookupCell} width='200px'
                          filterCell={createLookupFilterCell(context.cache.getBanTypes)} />
                        <Column field='validFrom' title={localize('Common.ValidFrom')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                        <Column field='validTo' title={localize('Common.ValidTo')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                      </PropertyRenderer>
                    </IndividualPermissionProvider>
                  </TabPane>
                  <TabPane tabId='5'>
                    <IndividualPermissionProvider individualPermission={individualPermissionTypes.enum.IndividualCbiEmployee}>
                      <PropertyRenderer name='jobs' inputType='grid' readonly
                        dataSource={getJobs}>
                        <Column field='organizationalUnit' title={localize('Individuals.OrganizationalUnit')} cell={LookupCell} width='200px' />
                        <Column field='jobPosition' title={localize('Individuals.JobPosition')} cell={LookupCell} width='200px' />
                        <Column field='validFrom' title={localize('Common.ValidFrom')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                        <Column field='validTo' title={localize('Common.ValidTo')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                        {BooleanColumn({ field: 'acting', title: localize('Individuals.ActingJob'), width: '100px' })}
                      </PropertyRenderer>
                    </IndividualPermissionProvider>
                  </TabPane>
                  <TabPane tabId='6'>
                    <IndividualPermissionProvider individualPermission={individualPermissionTypes.enum.IndividualCourses}>
                      <PropertyRenderer name='courses' inputType='grid'
                        addButtonLabel='Individuals.AddCourse'
                        detailDialog={{ dialogType: dialogTypes.course }}
                        dataSource={getCourses}>
                        <Column field='courseType' title={localize('Common.Type')} cell={LookupCell} width='200px' filterCell={createLookupFilterCell(context.cache.getCourseTypes)} />
                        <Column field='dateFrom' title={localize('Individuals.DateFrom')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                        <Column field='dateTo' title={localize('Individuals.DateTo')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                      </PropertyRenderer>
                    </IndividualPermissionProvider>
                  </TabPane>
                  <TabPane tabId='7'>
                    <IndividualPermissionProvider individualPermission={individualPermissionTypes.enum.IndividualEducation}>
                      <PropertyRenderer name='educations' inputType='grid'
                        addButtonLabel='Individuals.AddEducation'
                        detailDialog={{ dialogType: dialogTypes.education }}
                        dataSource={getEducations}>
                        <Column field='educationType' title={localize('Common.Type')} cell={LookupCell} width='200px' filterCell={createLookupFilterCell(context.cache.getEducationTypes)} />
                        <Column field='dateFrom' title={localize('Individuals.DateFrom')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                        <Column field='dateTo' title={localize('Individuals.DateTo')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                      </PropertyRenderer>
                    </IndividualPermissionProvider>
                  </TabPane>
                  <TabPane tabId='8'>
                    <IndividualPermissionProvider individualPermission={individualPermissionTypes.enum.IndividualExperience}>
                      <PropertyRenderer name='experiences' inputType='grid'
                        addButtonLabel='Individuals.AddExperience'
                        detailDialog={{ dialogType: dialogTypes.experience }}
                        dataSource={getExperiences}>
                        <Column field='experienceType' title={localize('Common.Type')} cell={LookupCell} width='200px' filterCell={createLookupFilterCell(context.cache.getExperienceTypes)} />
                        <Column field='dateFrom' title={localize('Individuals.DateFrom')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                        <Column field='dateTo' title={localize('Individuals.DateTo')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                      </PropertyRenderer>
                    </IndividualPermissionProvider>
                  </TabPane>
                  <TabPane tabId='9'>
                    <IndividualPermissionProvider individualPermission={individualPermissionTypes.enum.IndividualRelatedPersons}>
                      <PropertyRenderer
                        name='relatedPersons' inputType='grid'
                        addButtonLabel='Individuals.AddRelatedInstitution'
                        detailDialog={{ dialogType: dialogTypes.relatedInstitution }}
                        dataSource={getRelatedPersons}>
                        <Column field='institution' title={localize('Individuals.Institution')} cell={LookupCell} width='200px' />
                        {LookupNameColumn({ locale, field: 'relatedPersonType', title: localize('Individuals.RelatedPersonType'), editable: false, width: '200px' })}
                        <Column field='validFrom' title={localize('Common.ValidFrom')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                        <Column field='validTo' title={localize('Common.ValidTo')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='200px' />
                      </PropertyRenderer>
                    </IndividualPermissionProvider>
                  </TabPane>
                  <TabPane tabId='10'>
                    <IndividualGovernmentService id={targetId} />
                  </TabPane>
                  <TabPane tabId='11'>
                    <IndividualBlacklists />
                  </TabPane>
                  <TabPane tabId='12'>
                    <CbiBlacklists personId={targetId} />
                  </TabPane>
                  <TabPane tabId='13'>
                    <IndividualChangeRequestHistory dataSource={getChangeRequestHistory} />
                  </TabPane>
                  <TabPane tabId='14'>
                    <IndividualUserHistory dataSource={getUserAccountHistory} />
                  </TabPane>
                </TabContent>
              </WhiteBox>
            </Col>
          </Row>
        </ValidationBoundary>
      </MainBody>
    </Main>
  )
})

export {
  IndividualDetail
}



// WEBPACK FOOTER //
// ./src/internal-app/pages/individuals/detail.js